import * as React from 'react';
import { createPortal } from 'react-dom';

import {
  ALL,
  Pack,
  PackClassroom,
  manageActivitiesLink,
  toLegacyCaUnit,
} from './shared';
import { ClassCountTooltip, ModalInfoNotice } from './crossClassNotice';

import ActivityTable from '../manage_units/activity_table';
import ArchiveModal from '../manage_units/archive_modal';
import CloseUnitModal from '../manage_units/close_unit_modal';
import RenameModal from '../manage_units/rename_modal';
import ReopenUnitModal from '../manage_units/reopen_unit_modal';
import ShareActivityPackModal from '../create_unit/share_activity_pack/shareActivityPackModal';
import useModalAccessibility from '../../../../Shared/hooks/useModalAccessibility';

export const RENAME = 'rename'
export const ARCHIVE = 'archive'
export const SHARE = 'share'
export const CLOSE = 'close'
export const REOPEN = 'reopen'
export const EDIT_ACTIVITIES = 'editActivities'
export const SHARE_FROM_EDIT = 'shareFromEdit'

interface PackActionModalsProps {
  pack: Pack;
  selectedClassroomId: string | number;
  onPackChanged: (snackbarCopy?: string) => void;
  isDiagnostic?: boolean;
  companionPack?: Pack;
  showModal: string;
  setShowModal: React.Dispatch<React.SetStateAction<string>>;
  activityToShare: any;
  setActivityToShare: (activity: any) => void;
}

const PackActionModals = ({ pack, selectedClassroomId, onPackChanged, isDiagnostic, companionPack, showModal, setShowModal, activityToShare, setActivityToShare }: PackActionModalsProps) => {
  const [removeConfirmOpen, setRemoveConfirmOpen] = React.useState(false)

  function closeModal() { setShowModal('') }

  const editActivitiesOpen = showModal === EDIT_ACTIVITIES

  const { modalRef } = useModalAccessibility(
    () => { if (!removeConfirmOpen) { closeModal() } },
    editActivitiesOpen
  )

  // the modal is full-screen, so a live page scrollbar would sit on top of it and count as a click outside
  React.useEffect(() => {
    if (!editActivitiesOpen) { return }
    const root = document.documentElement
    const previousOverflow = root.style.overflow
    root.style.overflow = 'hidden'
    return () => { root.style.overflow = previousOverflow }
  }, [editActivitiesOpen])

  function modalCallbacks(kind: string) {
    const closeIfCurrent = () => setShowModal(current => (current === kind ? '' : current))
    return {
      handleSuccess: (snackbarCopy?: string) => {
        closeIfCurrent()
        onPackChanged(snackbarCopy)
      },
      handleFailure: (message?: string) => {
        closeIfCurrent()
        onPackChanged(message || 'Something went wrong. Not all changes were saved.')
      },
    }
  }
  const renameCallbacks = modalCallbacks(RENAME)
  const archiveCallbacks = modalCallbacks(ARCHIVE)
  const closeCallbacks = modalCallbacks(CLOSE)
  const reopenCallbacks = modalCallbacks(REOPEN)

  function onActivityTableSuccess(snackbarCopy?: string) {
    onPackChanged(snackbarCopy)
  }

  // ActivityTable resets its invalid-date highlights on any new `data` object — memoize so re-renders don't wipe them
  const legacyCaUnit = React.useMemo(() => toLegacyCaUnit(pack), [pack])

  function showShareModal() { setShowModal(SHARE_FROM_EDIT) }

  function closeShare() {
    setActivityToShare(null)
    if (showModal === SHARE_FROM_EDIT) {
      setShowModal(EDIT_ACTIVITIES)
    } else {
      closeModal()
    }
  }

  const companionUnitIds = companionPack?.ownedByCurrentUser && String(companionPack.unitId) !== String(pack.unitId) ? [companionPack.unitId] : []

  const companionIsOpen = companionPack ? companionPack.open !== false : undefined
  const companionWillClose = companionUnitIds.length > 0 && companionIsOpen === true
  const companionWillReopen = companionUnitIds.length > 0 && companionIsOpen === false

  const companionClassrooms = companionUnitIds.length > 0 && companionPack ? companionPack.classrooms : []
  const pairClassrooms = pack.classrooms.concat(
    companionClassrooms.filter(c => !pack.classrooms.some(pc => String(pc.id) === String(c.id)))
  )

  function modalNotice(classrooms: PackClassroom[], consequence: React.ReactNode, also?: React.ReactNode | false, alsoStandalone?: React.ReactNode | false) {
    const otherClassCount = selectedClassroomId === ALL ? 0 : classrooms.filter(c => String(c.id) !== String(selectedClassroomId)).length
    if (otherClassCount >= 1) {
      return (
        <ModalInfoNotice>
          This pack is assigned to <ClassCountTooltip classrooms={classrooms} />. {consequence}
          {also ? <React.Fragment> Also, {also}</React.Fragment> : null}
        </ModalInfoNotice>
      )
    }
    return alsoStandalone ? <ModalInfoNotice>{alsoStandalone}</ModalInfoNotice> : null
  }

  return (
    <React.Fragment>
      {editActivitiesOpen && createPortal(
        <div className="modal-container edit-activities-modal-container" style={removeConfirmOpen ? { display: 'none' } : undefined}>
          <div className="modal-background" />
          <div aria-labelledby={`editActivities${pack.unitId}`} aria-modal="true" className="quill-modal modal-body edit-activities-modal" ref={modalRef} role="dialog" tabIndex={-1}>
            <h3 className="title" id={`editActivities${pack.unitId}`}>Edit dates &amp; activities</h3>
            {modalNotice(pack.classrooms, 'Any changes you make apply to all of them.')}
            <div className="my-activities">
              <section className="activity-pack">
                <ActivityTable
                  data={legacyCaUnit}
                  datePickersInPortal={true}
                  dragOverlayInPortal={true}
                  handleActivityClicked={setActivityToShare}
                  handleToggleModal={showShareModal}
                  isOwner={Boolean(pack.ownedByCurrentUser)}
                  onRemoveModalToggle={setRemoveConfirmOpen}
                  onSuccess={onActivityTableSuccess}
                  renderRemoveModalInPortal={true}
                />
              </section>
            </div>
            <div className="form-buttons">
              {!isDiagnostic && <a className="quill-button outlined medium grey focus-on-light" href={manageActivitiesLink(pack.unitId, pack.unitName)}>Add activities</a>}
              <button className="quill-button contained medium green focus-on-light" onClick={closeModal} type="button">Done</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {showModal === RENAME && createPortal(
        <RenameModal
          closeModal={closeModal}
          notice={modalNotice(
            pack.classrooms,
            'Renaming it changes the name for all of them.',
            isDiagnostic && <React.Fragment>this card keeps the diagnostic&rsquo;s name on this page &mdash; the pack name you set here is what students see.</React.Fragment>,
            isDiagnostic && <React.Fragment>This card keeps the diagnostic&rsquo;s name on this page &mdash; the pack name you set here is what students see.</React.Fragment>
          )}
          onSuccess={renameCallbacks.handleSuccess}
          unitId={pack.unitId}
          unitName={pack.unitName}
        />,
        document.body
      )}
      {showModal === ARCHIVE && createPortal(
        <ArchiveModal
          additionalUnitIds={companionUnitIds}
          closeModal={closeModal}
          notice={modalNotice(
            pairClassrooms,
            'Deleting it removes it from all of them.',
            companionUnitIds.length > 0 && <React.Fragment>this permanently deletes both the <strong>Baseline Pre</strong> and the <strong>Growth Post</strong>.</React.Fragment>,
            companionUnitIds.length > 0 && <React.Fragment>This permanently deletes both the <strong>Baseline Pre</strong> and the <strong>Growth Post</strong>.</React.Fragment>
          )}
          onFailure={archiveCallbacks.handleFailure}
          onSuccess={archiveCallbacks.handleSuccess}
          unitId={pack.unitId}
          unitName={pack.unitName}
        />,
        document.body
      )}
      {showModal === CLOSE && createPortal(
        <CloseUnitModal
          additionalUnitIds={companionUnitIds}
          closedViewName="Closed Activity Packs"
          closeModal={closeModal}
          notice={modalNotice(
            pairClassrooms,
            'Closing it closes it for all of them.',
            companionWillClose && <React.Fragment>this closes both the <strong>Baseline Pre</strong> and the <strong>Growth Post</strong>.</React.Fragment>,
            companionWillClose && <React.Fragment>This closes both the <strong>Baseline Pre</strong> and the <strong>Growth Post</strong>.</React.Fragment>
          )}
          onFailure={closeCallbacks.handleFailure}
          onSuccess={closeCallbacks.handleSuccess}
          unitId={pack.unitId}
          unitName={pack.unitName}
        />,
        document.body
      )}
      {showModal === REOPEN && createPortal(
        <ReopenUnitModal
          additionalUnitIds={companionUnitIds}
          closeModal={closeModal}
          notice={modalNotice(
            pairClassrooms,
            'Reopening it reopens it for all of them.',
            companionWillReopen && <React.Fragment>this reopens both the <strong>Baseline Pre</strong> and the <strong>Growth Post</strong>.</React.Fragment>,
            companionWillReopen && <React.Fragment>This reopens both the <strong>Baseline Pre</strong> and the <strong>Growth Post</strong>.</React.Fragment>
          )}
          onFailure={reopenCallbacks.handleFailure}
          onSuccess={reopenCallbacks.handleSuccess}
          openViewName="Open Activity Packs"
          unitId={pack.unitId}
        />,
        document.body
      )}
      {(showModal === SHARE || showModal === SHARE_FROM_EDIT) && createPortal(
        <ShareActivityPackModal
          activityPackData={{ name: pack.unitName, activityCount: pack.activities.length, activities: [] }}
          closeModal={closeShare}
          selectableClassrooms={pack.classrooms.map(c => c.name)}
          selectedClassroomId={String(selectedClassroomId)}
          singleActivity={activityToShare}
          unitId={pack.unitId}
        />,
        document.body
      )}
    </React.Fragment>
  )
}

export default PackActionModals
